import {
  ApolloClient,
  ApolloLink,
  createQueryPreloader,
  from,
  InMemoryCache,
  ServerParseError
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import { env } from 'config'
import bugsnagClient from 'config/bugsnag'
import {
  AuthPath,
  LocationPaths,
  OwnSensorV2Path,
  PaidVideoCallsPath,
  PartnerPath,
  SearchParams,
  UhcPath,
  VideoCallsPath
} from 'location.types'
import { matchPath } from 'react-router'
import { clientLink } from './clientLink'
import { userIdLink } from './userIdLink'

export const uri = env.REACT_APP_API_ENDPOINT
const redirectToLoginErrorCodes = [401, 403]
const invalidSessionErrorCode = 401

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (graphQLErrors) {
    const errorMessage = graphQLErrors[0].message
    if (
      errorMessage === 'invalid_mfa_token' &&
      window.location.pathname !== LocationPaths.MfaChallenge
    ) {
      window.location.replace(
        `${LocationPaths.MfaChallenge}?${SearchParams.TargetPath}=${window.location.pathname}`
      )
    }
  }

  if (networkError) {
    if ((networkError as ServerParseError).statusCode !== invalidSessionErrorCode) {
      bugsnagClient.notify(networkError)
    }
    if (redirectToLoginErrorCodes.includes((networkError as ServerParseError).statusCode)) {
      maybeNavigateToLogin()
    }
  }
})

const PUBLIC_PATHS: string[] = [
  LocationPaths.Auth,
  `${LocationPaths.Auth}/${AuthPath.Confirm}`,
  LocationPaths.SignIn,
  LocationPaths.Questionnaire,
  LocationPaths.DownloadApp,
  LocationPaths.LegacyOwnSensor,
  LocationPaths.OwnSensorV2,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.LoadingScreen}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.ChooseSensor}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.LibreIndirectSyncConfirmation}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.WhatBringsYou}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Quote1}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.TrackedGlucose}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.AppOverview}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Height}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Weight}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.WellnessSnapshot}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.GoalWeight}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Quote2}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.ChronicConditions}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.FamilyDiabetes}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.Quote3}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.PreparingYourProgram}`,
  `${LocationPaths.OwnSensorV2}/${OwnSensorV2Path.CreateAccount}`,
  LocationPaths.VideoCalls,
  // Retired public URLs supported by compatibility redirects.
  `${LocationPaths.VideoCalls}/welcome`,
  `${LocationPaths.VideoCalls}/post-health-goal`,
  `${LocationPaths.VideoCalls}/post-add-appointment-agenda`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.MfpCreditIntro}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.State}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.SignUp}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.SignIn}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.ScheduleCall}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.AddAppointmentAgenda}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.InsurancePrep}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.Insurance}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.EmailAddress}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.State}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.ChoosePrimaryHealthGoal}`,
  `${LocationPaths.VideoCalls}/${VideoCallsPath.ChooseSecondaryHealthGoal}`,
  LocationPaths.PaidVideoCalls,
  `${LocationPaths.PaidVideoCalls}/${PaidVideoCallsPath.UserInfo}`,
  LocationPaths.Partner,
  `${LocationPaths.Partner}/${PartnerPath.SignUp}`,
  `${LocationPaths.Partner}/${PartnerPath.SignIn}`,
  `${LocationPaths.Partner}/${PartnerPath.CodeRedemption}`,
  `${LocationPaths.Uhc}/${UhcPath.Auth}`,
  `${LocationPaths.Uhc}/${UhcPath.Error}`,
  `${LocationPaths.Uhc}/${UhcPath.SignUp}`,
  `${LocationPaths.Uhc}/${UhcPath.SignIn}`
]

const maybeNavigateToLogin = () => {
  const pathname = window.location.pathname.endsWith('/')
    ? window.location.pathname.slice(0, -1)
    : window.location.pathname

  if (PUBLIC_PATHS.some((path) => matchPath(path, pathname))) {
    return
  }

  const searchParams = new URLSearchParams(window.location.search)

  const token = searchParams.get(SearchParams.Token)

  if (token) {
    window.location.replace(
      `${LocationPaths.Auth}?token=${token}&target_path=${window.location.pathname}`
    )
  } else {
    window.location.replace(`${LocationPaths.SignIn}?target_path=${window.location.pathname}`)
  }
}

const uploadLink = createUploadLink({
  uri,
  credentials: 'include'
}) as unknown as ApolloLink

export const client = new ApolloClient({
  link: from([clientLink, userIdLink, errorLink, uploadLink]),
  cache: new InMemoryCache()
})

export const cache = client.cache

export const preloadQuery = createQueryPreloader(client)
